import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IoEyeOffOutline, IoEyeOutline } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import MasterFormPage from "../common/MasterFormPage";
import MasterShell from "../common/MasterShell";
import MasterToast from "../common/MasterToast";
import { getRoles } from "../../services/roleAccessService";
import { getDistricts } from "../../services/districtService";
import { getStations } from "../../services/stationService";
import {
  createStationUser,
  getStationUserById,
  updateStationUser,
} from "../../services/stationUserService";
import { getSubDivisions } from "../../services/subDivisionService";
import {
  extractApiErrorMessage,
  getDefaultUserState,
  getStatusFormValue,
  passwordRegex,
  preferredLanguageOptions,
  statusOptions,
  toSelectOptions,
} from "../../utils/masterHelpers";
import { useCapabilities } from "../../contexts/CapabilitiesContext";
import {
  getMasterHierarchyAccess,
  roleNeedsDistrict,
  roleNeedsStation,
  roleNeedsSubDivision,
} from "../../utils/hierarchyScope";
import "./Users.css";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const createInitialValues = () => ({
  district_id: "",
  email: "",
  is_active: "1",
  name: "",
  password: "",
  preferred_language: "",
  role: "",
  state: getDefaultUserState(),
  station_id: "",
  sub_division_id: "",
  username: "",
});

const buildUserPayload = (
  values,
  assignableRoles,
  scopeAccess,
  { includeStatus = false } = {}
) => {
  const districtId = scopeAccess.canSelectDistrict
    ? values.district_id || null
    : scopeAccess.fixedDistrictId || null;
  const subDivisionId = scopeAccess.canSelectSubDivision
    ? values.sub_division_id || null
    : scopeAccess.fixedSubDivisionId || null;
  const stationId = scopeAccess.canSelectStation
    ? values.station_id || null
    : scopeAccess.fixedStationId || null;

  const payload = {
    district_id: roleNeedsDistrict(values.role, assignableRoles)
      ? districtId
      : null,
    email: values.email.trim(),
    name: values.name.trim(),
    preferred_language: values.preferred_language,
    role: values.role,
    state: values.state.trim(),
    station_id: roleNeedsStation(values.role, assignableRoles)
      ? stationId
      : null,
    sub_division_id: roleNeedsSubDivision(values.role, assignableRoles)
      ? subDivisionId
      : null,
    username: values.username.trim(),
  };

  if (values.password.trim()) {
    payload.password = values.password;
  }

  if (includeStatus) {
    payload.is_active = Number(values.is_active);
  }

  return payload;
};

export default function UserForm({ mode }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    assignableRoles,
    loading: capabilitiesLoading,
    user,
  } = useCapabilities();
  const [allStations, setAllStations] = useState([]);
  const [allSubDivisions, setAllSubDivisions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [roleOptions, setRoleOptions] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({
    message: "",
    open: false,
    severity: "success",
  });
  const [values, setValues] = useState(createInitialValues);
  const isEditMode = mode === "edit";

  const scopeAccess = useMemo(
    () => getMasterHierarchyAccess({ user }),
    [user]
  );
  const {
    canSelectDistrict,
    canSelectStation,
    canSelectSubDivision,
    fixedDistrictId,
    fixedStationId,
    fixedSubDivisionId,
  } = scopeAccess;

  const effectiveDistrictId = canSelectDistrict
    ? values.district_id
    : fixedDistrictId;
  const effectiveSubDivisionId = canSelectSubDivision
    ? values.sub_division_id
    : fixedSubDivisionId;
  const effectiveStationId = canSelectStation
    ? values.station_id
    : fixedStationId;

  const effectiveRoleOptions = useMemo(() => {
    if (assignableRoles.length > 0) {
      return assignableRoles
        .map((role) => ({
          label: role.label || role.key?.replace(/_/g, " ") || "-",
          value: role.key || "",
        }))
        .filter((option) => option.value);
    }

    return roleOptions;
  }, [assignableRoles, roleOptions]);

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const [districts, roles, subDivisions, stations] = await Promise.all([
        canSelectDistrict ? getDistricts() : Promise.resolve([]),
        getRoles(),
        canSelectSubDivision
          ? getSubDivisions(
              fixedDistrictId ? { district_id: fixedDistrictId } : {}
            )
          : Promise.resolve([]),
        canSelectStation
          ? getStations(
              fixedSubDivisionId
                ? {
                    sub_division_id: fixedSubDivisionId,
                    ...(fixedDistrictId ? { district_id: fixedDistrictId } : {}),
                  }
                : fixedDistrictId
                ? { district_id: fixedDistrictId }
                : {}
            )
          : Promise.resolve([]),
      ]);

      setDistrictOptions(toSelectOptions(districts));
      setRoleOptions(
        (Array.isArray(roles) ? roles : [])
          .filter(
            (role) =>
              role?.is_active === undefined ||
              role?.is_active === 1 ||
              role?.is_active === true
          )
          .map((role) => ({
            label: role?.label || role?.key?.replace(/_/g, " ") || "-",
            value: role?.key || "",
          }))
          .filter((role) => role.value)
      );
      setAllSubDivisions(Array.isArray(subDivisions) ? subDivisions : []);
      setAllStations(Array.isArray(stations) ? stations : []);

      if (isEditMode) {
        const selectedUser = await getStationUserById(id);

        if (!selectedUser) {
          setToast({
            message: "User not found.",
            open: true,
            severity: "error",
          });
        } else {
          setValues({
            district_id: selectedUser.district_id || "",
            email: selectedUser.email || "",
            is_active: getStatusFormValue(selectedUser.is_active) || "1",
            name: selectedUser.name || "",
            password: "",
            preferred_language: selectedUser.preferred_language || "",
            role: selectedUser.role || "",
            state: selectedUser.state || "",
            station_id: selectedUser.station_id || "",
            sub_division_id: selectedUser.sub_division_id || "",
            username: selectedUser.username || "",
          });
        }
      }
    } catch (apiError) {
      setToast({
        message: extractApiErrorMessage(
          apiError,
          "Failed to load user details."
        ),
        open: true,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [
    canSelectDistrict,
    canSelectStation,
    canSelectSubDivision,
    fixedDistrictId,
    fixedSubDivisionId,
    id,
    isEditMode,
  ]);

  useEffect(() => {
    if (capabilitiesLoading) {
      return;
    }

    loadData();
  }, [capabilitiesLoading, loadData]);

  useEffect(() => {
    if (isEditMode || capabilitiesLoading) {
      return;
    }

    setValues((currentValues) => ({
      ...currentValues,
      district_id: canSelectDistrict ? currentValues.district_id : fixedDistrictId,
      station_id: canSelectStation ? currentValues.station_id : fixedStationId,
      sub_division_id: canSelectSubDivision
        ? currentValues.sub_division_id
        : fixedSubDivisionId,
    }));
  }, [
    capabilitiesLoading,
    canSelectDistrict,
    canSelectStation,
    canSelectSubDivision,
    fixedDistrictId,
    fixedStationId,
    fixedSubDivisionId,
    isEditMode,
  ]);

  const filteredSubDivisions = useMemo(() => {
    const items = effectiveDistrictId
      ? allSubDivisions.filter(
          (item) => item.district_id === effectiveDistrictId
        )
      : allSubDivisions;

    return toSelectOptions(items);
  }, [allSubDivisions, effectiveDistrictId]);

  const filteredStations = useMemo(() => {
    const items = allStations.filter((item) => {
      if (effectiveDistrictId && item.district_id !== effectiveDistrictId) {
        return false;
      }

      if (
        effectiveSubDivisionId &&
        item.sub_division_id !== effectiveSubDivisionId
      ) {
        return false;
      }

      return true;
    });

    return toSelectOptions(items);
  }, [allStations, effectiveDistrictId, effectiveSubDivisionId]);

  const computedRoleOptions = useMemo(() => {
    if (
      !values.role ||
      effectiveRoleOptions.some((option) => option.value === values.role)
    ) {
      return effectiveRoleOptions;
    }

    return [
      ...effectiveRoleOptions,
      {
        label: values.role.replace(/_/g, " "),
        value: values.role,
      },
    ];
  }, [effectiveRoleOptions, values.role]);

  const fields = useMemo(
    () => [
      {
        name: "district_id",
        label: "District",
        placeholder: "Select district",
        required: roleNeedsDistrict(values.role, assignableRoles),
        type: "select",
        options: districtOptions,
        error: errors.district_id,
        disabled: isEditMode,
        hidden: !canSelectDistrict,
      },
      {
        name: "sub_division_id",
        label: "Sub-division",
        placeholder: "Select sub-division",
        required: roleNeedsSubDivision(values.role, assignableRoles),
        type: "select",
        options: filteredSubDivisions,
        error: errors.sub_division_id,
        disabled:
          isEditMode ||
          !roleNeedsDistrict(values.role, assignableRoles) ||
          !effectiveDistrictId,
        hidden: !canSelectSubDivision,
      },
      {
        name: "station_id",
        label: "Station",
        placeholder: "Select station",
        required: roleNeedsStation(values.role, assignableRoles),
        type: "select",
        options: filteredStations,
        error: errors.station_id,
        disabled:
          isEditMode ||
          !roleNeedsDistrict(values.role, assignableRoles) ||
          (roleNeedsSubDivision(values.role, assignableRoles) &&
            !effectiveSubDivisionId),
        hidden: !canSelectStation,
      },
      {
        name: "name",
        label: "Name",
        placeholder: "Enter full name",
        required: true,
        error: errors.name,
      },
      {
        name: "username",
        label: "Username",
        placeholder: "Enter username",
        required: true,
        error: errors.username,
        disabled: isEditMode,
      },
      {
        name: "email",
        label: "Email",
        placeholder: "Enter email address",
        required: true,
        type: "email",
        error: errors.email,
      },
      {
        name: "password",
        label: "Password",
        placeholder: "Enter password",
        required: !isEditMode,
        type: showPassword ? "text" : "password",
        hidden: isEditMode,
        error: errors.password,
        hint:
          "Minimum 8 characters with uppercase, lowercase, number, and special character.",
        actionIcon: showPassword ? <IoEyeOffOutline /> : <IoEyeOutline />,
        actionLabel: showPassword ? "Hide password" : "Show password",
        onActionClick: () => setShowPassword((currentValue) => !currentValue),
      },
      {
        name: "role",
        label: "Role",
        placeholder: "Select role",
        required: true,
        type: "select",
        options: computedRoleOptions,
        error: errors.role,
      },
      {
        name: "preferred_language",
        label: "Preferred Language",
        placeholder: "Select preferred language",
        required: true,
        type: "select",
        options: preferredLanguageOptions,
        hidden: isEditMode,
        error: errors.preferred_language,
      },
      {
        name: "state",
        label: "State",
        placeholder: "Enter state name",
        required: true,
        hidden: isEditMode,
        error: errors.state,
      },
      {
        name: "is_active",
        label: "Status",
        placeholder: "Select status",
        required: isEditMode,
        type: "select",
        options: statusOptions,
        hidden: !isEditMode,
        error: errors.is_active,
      },
    ],
    [
      assignableRoles,
      canSelectDistrict,
      canSelectStation,
      canSelectSubDivision,
      computedRoleOptions,
      districtOptions,
      effectiveDistrictId,
      effectiveSubDivisionId,
      errors.district_id,
      errors.email,
      errors.is_active,
      errors.name,
      errors.password,
      errors.preferred_language,
      errors.role,
      errors.state,
      errors.station_id,
      errors.sub_division_id,
      errors.username,
      filteredStations,
      filteredSubDivisions,
      isEditMode,
      showPassword,
      values.role,
    ]
  );

  const validate = () => {
    const nextErrors = {};

    if (!values.name.trim()) {
      nextErrors.name = "Name is required.";
    }

    if (!values.username.trim()) {
      nextErrors.username = "Username is required.";
    }

    if (!values.email.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!emailRegex.test(values.email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!values.password.trim() && !isEditMode) {
      nextErrors.password = "Password is required.";
    } else if (values.password.trim() && !passwordRegex.test(values.password)) {
      nextErrors.password =
        "Password must be at least 8 characters and include uppercase, lowercase, number, and special character.";
    }

    if (!values.role) {
      nextErrors.role = "Role is required.";
    }

    if (
      roleNeedsDistrict(values.role, assignableRoles) &&
      !effectiveDistrictId
    ) {
      nextErrors.district_id = "District is required for this role.";
    }

    if (
      roleNeedsSubDivision(values.role, assignableRoles) &&
      !effectiveSubDivisionId
    ) {
      nextErrors.sub_division_id = "Sub-division is required for this role.";
    }

    if (
      roleNeedsStation(values.role, assignableRoles) &&
      !effectiveStationId
    ) {
      nextErrors.station_id = "Station is required for this role.";
    }

    if (!isEditMode && !values.preferred_language) {
      nextErrors.preferred_language = "Preferred language is required.";
    }

    if (!isEditMode && !values.state.trim()) {
      nextErrors.state = "State is required.";
    }

    if (isEditMode && !values.is_active) {
      nextErrors.is_active = "Status is required.";
    }

    setErrors({
      ...nextErrors,
      ...(canSelectDistrict ? {} : { district_id: "" }),
      ...(canSelectSubDivision ? {} : { sub_division_id: "" }),
      ...(canSelectStation ? {} : { station_id: "" }),
    });

    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setValues((currentValues) => {
      const nextValues = {
        ...currentValues,
        [name]: value,
      };

      if (name === "district_id") {
        nextValues.sub_division_id = "";
        nextValues.station_id = "";
      }

      if (name === "sub_division_id") {
        nextValues.station_id = "";
      }

      if (name === "role") {
        if (!roleNeedsDistrict(value, assignableRoles)) {
          nextValues.district_id = "";
          nextValues.sub_division_id = "";
          nextValues.station_id = "";
        } else if (!roleNeedsSubDivision(value, assignableRoles)) {
          nextValues.sub_division_id = "";
          nextValues.station_id = "";
        } else if (!roleNeedsStation(value, assignableRoles)) {
          nextValues.station_id = "";
        }
      }

      return nextValues;
    });

    setErrors((currentErrors) => ({
      ...currentErrors,
      [name]: "",
      ...(name === "district_id"
        ? { station_id: "", sub_division_id: "" }
        : {}),
      ...(name === "sub_division_id" ? { station_id: "" } : {}),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    setSubmitting(true);

    try {
      if (isEditMode) {
        const response = await updateStationUser(
          id,
          buildUserPayload(values, assignableRoles, scopeAccess, {
            includeStatus: true,
          })
        );

        navigate("/users", {
          replace: true,
          state: {
            toast: {
              message: response?.message || "User updated successfully.",
              severity: "success",
            },
          },
        });
      } else {
        const response = await createStationUser(
          buildUserPayload(values, assignableRoles, scopeAccess)
        );

        navigate("/users", {
          replace: true,
          state: {
            toast: {
              message: response?.message || "User created successfully.",
              severity: "success",
            },
          },
        });
      }
    } catch (apiError) {
      setToast({
        message: extractApiErrorMessage(
          apiError,
          isEditMode ? "Failed to update user." : "Failed to create user."
        ),
        open: true,
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MasterShell pageClassName="users-module">
      {loading || capabilitiesLoading ? (
        <div className="masters-state">
          <div className="masters-state__loader" />
          <h2 className="masters-state__title">Loading user</h2>
          <p className="masters-state__text">
            Please wait while the user data is being prepared.
          </p>
        </div>
      ) : (
        <MasterFormPage
          description={
            isEditMode
              ? "Update user details, assignments, role, and active status from one form."
              : "Create station users with dependent district, sub-division, and station selection."
          }
          fields={fields}
          onBack={() => navigate("/users")}
          onChange={handleChange}
          onSubmit={handleSubmit}
          submitDisabled={false}
          submitLabel={isEditMode ? "Save User" : "Save User"}
          submitting={submitting}
          title={isEditMode ? "Edit User" : "Add User"}
          values={values}
        />
      )}

      <MasterToast
        message={toast.message}
        onClose={() =>
          setToast((currentToast) => ({ ...currentToast, open: false }))
        }
        open={toast.open}
        severity={toast.severity}
      />
    </MasterShell>
  );
}
